import {
  visibleFields,
  type QuestionnaireAnswers,
  type QuestionnaireStep,
} from "@repo/questionnaire";
import type { FormVisitAnswerInput } from "@repo/api/modules/os/lib/form-visit-details";

const summaryKeys = new Set([
  "intake.business_type",
  "intake.ownership_duration",
  "intake.annual_revenue",
  "intake.sell_motivation",
  "intake.seller_timeline",
  "intake.contact_preference",
]);

/** Send known business answers only; never serialize the full form or contact fields. */
export function collectFormVisitAnswers(
  steps: QuestionnaireStep[],
  answers: QuestionnaireAnswers,
): FormVisitAnswerInput[] {
  return steps
    .flatMap((step) => visibleFields(step, answers))
    .flatMap((field): FormVisitAnswerInput[] => {
      if (
        !summaryKeys.has(field.analyticsKey ?? "") ||
        field.sensitivity !== "standard"
      )
        return [];
      const value = answers[field.binding];
      if (
        field.kind === "business_type" &&
        typeof value === "string" &&
        value.trim().length >= 2
      ) {
        return [
          {
            fieldId: field.id,
            value: value.trim().replace(/\s+/g, " ").slice(0, 100),
          },
        ];
      }
      if (
        field.kind === "business_type" &&
        value &&
        typeof value === "object" &&
        "id" in value
      ) {
        const id = Number(value.id);
        return Number.isSafeInteger(id) && id > 0
          ? [{ fieldId: field.id, value: id }]
          : [];
      }
      if (
        field.kind === "enum" &&
        typeof value === "string" &&
        field.options.some((option) => option.id === value)
      ) {
        return [{ fieldId: field.id, value }];
      }
      if (field.kind === "multi_select" && Array.isArray(value)) {
        return [
          {
            fieldId: field.id,
            value: field.options
              .filter((option) => value.includes(option.id))
              .map((option) => option.id),
          },
        ];
      }
      if (
        field.kind === "duration" &&
        value &&
        typeof value === "object" &&
        "amount" in value &&
        "unit" in value &&
        typeof value.amount === "number" &&
        Number.isInteger(value.amount) &&
        value.amount >= 0 &&
        value.amount <= 600 &&
        (value.unit === "years" || value.unit === "months")
      ) {
        return [
          {
            fieldId: field.id,
            value: { amount: value.amount, unit: value.unit },
          },
        ];
      }
      return [];
    })
    .slice(0, 6);
}
